import React, { useEffect, useContext } from "react"
import moment from "moment-timezone"
import { BookingProvider, BookingContext } from "../../utils/providers/isolated-booking"
import { Redirect, Switch, Route, useRouteMatch } from "react-router-dom"

import PortalShell from "./portal-shell"
import ScrollToTop from "./scroll-to-top"

/**
 * Portal screens — all on the redesigned design system.
 */
import Home from "./tabs/home"
import Timeline from "./tabs/timeline"
import Pricing from "./tabs/pricing"
import Payments from "./tabs/payments"
import Payment from "./tabs/payment"
import CheckInTimes from "./tabs/checkins"
import Nearby from "./tabs/nearby"
import Stableford from "./tabs/stableford"
import Messages from "./tabs/messages"

/**
 * Pin every moment(...) call inside the Manage My Booking portal to UK time.
 * Runs at module load — only happens when a client visits the portal, so it
 * does not affect any other area of the app. Clients abroad will see the same
 * times the UK office entered into Firestore.
 */
moment.tz.setDefault("Europe/London")

/**
 * Portal for a single booking. Renders the shared shell with the routed
 * section inside it.
 */
function MyBooking({ bookingID }) {
	const { setBookingID } = useContext(BookingContext)
	const { path } = useRouteMatch()

	useEffect(() => {
		bookingID && setBookingID(bookingID)
	}, [bookingID, setBookingID])

	return (
		<PortalShell bookingID={bookingID}>
			<ScrollToTop />
			<Switch>
				<Route
					path={path}
					exact
					component={Home}
				/>
				<Route
					path={`${path}/itinerary`}
					component={Timeline}
				/>
				<Route
					path={`${path}/pricing`}
					component={Pricing}
				/>
				<Route
					path={`${path}/payments`}
					component={Payments}
				/>
				<Route
					path={`${path}/payment`}
					component={Payment}
				/>
				<Route
					path={`${path}/checkin`}
					component={CheckInTimes}
				/>
				<Route
					path={`${path}/nearby`}
					component={Nearby}
				/>
				<Route
					path={`${path}/stableford`}
					component={Stableford}
				/>
				<Route
					path={`${path}/messages`}
					component={Messages}
				/>
			</Switch>
		</PortalShell>
	)
}

/**
 * Wrap the portal in the isolated booking provider, redirecting to the client
 * login if no booking ID is present in the URL.
 */
export default function MyBookingWrapper(props) {
	const bookingID = props.match.params.id

	if (!bookingID) {
		return <Redirect to="/mybooking" />
	}

	return (
		<BookingProvider>
			<MyBooking bookingID={bookingID} />
		</BookingProvider>
	)
}
